import React from "react";

// Article 9 — Hepatitis B–Positive Partner in Pregnancy: Can His High Viral Load Affect Me or My Baby?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

export function HepatitisBPositivePartnerPregnancy() {
  return (
    <article className="mt-10">
      <P>
        A partner’s high hepatitis B viral load does not pass directly from him to the unborn baby. The concern is whether the pregnant woman is already infected, protected by immunity or still susceptible to acquiring hepatitis B through sex or blood exposure.
      </P>
      <P>
        The useful question is: What exactly is positive in the partner, what do the mother’s own tests show, and is vaccination or post-exposure protection still needed?
      </P>

      <H2>“High Positive” Is Not One Hepatitis B Result</H2>
      <P>
        Reports may describe hepatitis B surface antigen—HBsAg—as positive, hepatitis B e antigen—HBeAg—as positive, or hepatitis B virus DNA as a number. These results do not mean the same thing. HBsAg indicates current infection. HBeAg often suggests greater infectivity, while quantitative HBV DNA directly measures how much virus is circulating. A laboratory index reported as “strongly positive” is not automatically the same as a high viral load. The exact test name and HBV DNA value should therefore be reviewed rather than relying on the word “titre.” <Cite n="1" />
      </P>

      <H2>The Partner Does Not Directly Infect the Fetus</H2>
      <P>
        Hepatitis B is not inherited from the father and does not travel directly from his bloodstream into the fetus. The clinically important route is partner-to-mother transmission through infected blood, semen or other body fluids, followed—if the mother becomes infected—by possible mother-to-child transmission around birth. A higher HBV DNA level can make the partner more infectious, but it does not prove that the mother or baby is infected. Hepatitis B is not usually spread by hugging, coughing, sharing food or ordinary household contact. <Cite n="2" />
      </P>

      <H2>The Pregnant Woman Needs Her Own Three-Part Answer</H2>
      <P>
        Every pregnant woman should be screened for HBsAg during each pregnancy. When her sexual partner has hepatitis B, a one-time triple panel—HBsAg, anti-HBs and total anti-HBc—helps distinguish current infection, immunity and susceptibility. <Cite n="1" /> HBsAg positivity suggests current maternal infection. Anti-HBs after vaccination indicates immunity; a documented level of at least 10 mIU/mL after a complete vaccine series is considered protective. If HBsAg, anti-HBs and total anti-HBc are all negative, she is susceptible and needs protection rather than reassurance from the partner’s report alone. <Cite n="1,2" /> If she remains susceptible while exposure continues, repeat HBsAg testing later in pregnancy or at delivery may be needed. <Cite n="2" />
      </P>

      <H2>Recent Unprotected Exposure Needs Prompt Review</H2>
      <P>
        A susceptible pregnant woman who has had recent unprotected sex, a needlestick or blood contact from an HBsAg-positive partner should seek medical advice immediately. Do not wait for symptoms or for a routine antenatal appointment. For a significant sexual exposure, hepatitis B immune globulin—HBIG—and hepatitis B vaccination may be recommended as soon as possible. Post-exposure protection works best promptly; available guidance considers HBIG unlikely to remain effective beyond 14 days after sexual exposure. Testing should not delay time-sensitive protection. <Cite n="3,4" />
      </P>

      <H2>Pregnancy Is Not a Reason to Delay Vaccination</H2>
      <P>
        Hepatitis B vaccine is non-live and pregnancy is not a contraindication. A susceptible woman can begin or complete vaccination during pregnancy. <Cite n="3" /> Until protection is confirmed, condoms should be used and razors, toothbrushes or anything that may carry blood should not be shared. Cuts should be covered. Separate utensils, separate rooms and social isolation are unnecessary. Because an infected partner creates ongoing exposure, anti-HBs testing after the completed vaccine series may be recommended to document response. <Cite n="2" />
      </P>

      <H2>If the Mother Tests Positive, the Pregnancy Plan Changes</H2>
      <P>
        If the mother is HBsAg positive, her own HBV DNA—not the partner’s viral load—determines the pregnancy transmission plan. Assessment usually includes liver enzymes, HBV DNA and specialist review. WHO recommends tenofovir disoproxil fumarate prophylaxis for HBsAg-positive pregnant women with HBV DNA at or above 200,000 IU/mL, or positive HBeAg where HBV DNA testing is unavailable, to reduce mother-to-child transmission. The timing and duration must be individualised; treatment should not be started or stopped from an internet article. <Cite n="5" />
      </P>

      <H2>The Baby’s Plan Depends on the Mother’s Result</H2>
      <P>
        If the mother remains HBsAg negative, the father’s infection alone does not make the baby an exposed newborn and does not by itself create an indication for HBIG. The baby should still receive routine hepatitis B vaccination beginning at birth. If the mother is HBsAg positive—or her status is unknown at delivery—the birth team needs a written plan. The newborn should receive hepatitis B vaccine and HBIG urgently after birth, complete the vaccine series and undergo follow-up testing for HBsAg and anti-HBs at the recommended age. <Cite n="6" />
      </P>

      <P>
        The mother should seek prompt assessment for jaundice, dark urine, marked weakness, persistent vomiting or any acute illness after exposure. Reduced fetal movements require maternity assessment regardless of the suspected cause.
      </P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed font-semibold">
          Dr Tania’s takeaway: A partner’s high hepatitis B viral load matters because it may increase transmission to a non-immune mother—not because it directly infects the fetus. Test the mother, protect her promptly if she is susceptible, and base the newborn plan on her result rather than on the partner’s report alone.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Centers for Disease Control and Prevention. Clinical Testing and Diagnosis for Hepatitis B. Updated January 31, 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.cdc.gov/hepatitis-b/hcp/diagnosis-testing/index.html" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.cdc.gov/hepatitis-b/hcp/diagnosis-testing/index.html</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Centers for Disease Control and Prevention. Hepatitis B Virus (HBV) Infection. Sexually Transmitted Infections Treatment Guidelines, 2021. Current online guidance.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.cdc.gov/std/treatment-guidelines/hbv.htm" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.cdc.gov/std/treatment-guidelines/hbv.htm</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Centers for Disease Control and Prevention. Guidelines for Vaccinating Pregnant Women: Hepatitis B. Current online guidance.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.cdc.gov/vaccines-pregnancy/hcp/vaccination-guidelines/index.html" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.cdc.gov/vaccines-pregnancy/hcp/vaccination-guidelines/index.html</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Centers for Disease Control and Prevention. Epidemiology and Prevention of Vaccine-Preventable Diseases. Chapter 10: Hepatitis B. Current online Pink Book chapter.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.cdc.gov/pinkbook/hcp/table-of-contents/chapter-10-hepatitis-b.html" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.cdc.gov/pinkbook/hcp/table-of-contents/chapter-10-hepatitis-b.html</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] World Health Organization. Guidelines for the prevention, diagnosis, care and treatment for people with chronic hepatitis B infection. 2024. Section 7: Preventing mother-to-child transmission using antiviral prophylaxis.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.ncbi.nlm.nih.gov/books/NBK615006/" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.ncbi.nlm.nih.gov/books/NBK615006/</a>
          </p>
        </li>
        <li id="ref-6" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[6] Centers for Disease Control and Prevention. Clinical Guidance for Perinatal Hepatitis B Testing. Updated January 31, 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a href="https://www.cdc.gov/hepatitis-b/hcp/perinatal-provider-overview/clinical-testing-guidelines.html" target="_blank" rel="noopener noreferrer" className="text-prestige hover:underline break-all">https://www.cdc.gov/hepatitis-b/hcp/perinatal-provider-overview/clinical-testing-guidelines.html</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific obstetric, hepatology, infectious-disease or paediatric advice. Do not delay assessment after recent unprotected sexual or blood exposure, and do not start antiviral treatment or assume immunity from a past vaccine without reviewing the relevant tests. Seek urgent care for jaundice, dark urine, severe vomiting, marked weakness, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
